import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { AddIconComponent } from '@kuiper/icons';
import { RequestComponentService } from '../services/request-component/request-component.service';
import { ContributorsService } from '../services/contributors/contributors.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { Status } from '../../../../common/enums/user-status';
import { Meta } from '../../../../common/models/api-response.model';
import { Option } from '../../../../common/models/option.model';
import { RequestPosition } from '../../../../common/models/request-details.model';
import { DropdownListComponent } from '../../../../common/components/dropdown-list/dropdown-list.component';
import { ParallelStepStatusPipe } from '../../../../common/pipes/parallel-step-status/parallel-step-status.pipe';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { LookupService } from '../../../../common/services/lookup/lookup.service';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { LoaderComponent } from '../../../../../ui-components/lib/loader/loader.component';
import { StepperComponent } from '../../../../../ui-components/lib/stepper/stepper.component';
import { StepperOption } from '../../../../../ui-components/lib/stepper/stepper-options.interface';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';

@Component({
  selector: 'lib-request-contributors',
  imports: [
    StepperComponent,
    BadgeComponent,
    StepStatusPipe,
    ParallelStepStatusPipe,
    UserDropdownViewComponent,
    TranslocoPipe,
    AddIconComponent,
    NgTemplateOutlet,
    DropdownListComponent,
    LoaderComponent,
  ],
  templateUrl: './request-contributors.component.html',
  styleUrl: './request-contributors.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestContributorsComponent implements OnInit {
  private readonly requestComponentService = inject(RequestComponentService);
  private readonly lookupService = inject(LookupService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contributorsService = inject(ContributorsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );

  flowId = input.required<string>();

  private readonly requestType = this.activatedRoute.snapshot.params[
    'requestAction'
  ] as string;
  readonly isRequestAllowAction = this.requestType === RequestTypeEnum.ACTION;
  readonly currentStepId = this.activatedRoute.snapshot.params['stepId'];

  positions = signal<RequestPosition[]>([]);
  userDropdownList = signal<Option[]>([]);
  userDropdownMeta = signal<Meta | null>(null);
  contributorsDropdownList = signal<Option[]>([]);
  contributorsDropdownMeta = signal<Meta | null>(null);
  isLoadingDropdown = signal(false);
  isInAddMode = signal(false);
  StepStatusEnum = signal(StepStatusEnum).asReadonly();

  stepperOptions = computed<StepperOption[]>(() =>
    this.requestComponentService.drawStepperByPosition(this.positions())
  );

  isLoadingPositions = signal(false);
  isApplyingActionContributor = signal(false);

  ngOnInit() {
    this.getPositions();
  }

  getPositions() {
    this.isLoadingPositions.set(true);
    this.contributorsService
      .getPositionsWithContributors(this.flowId())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoadingPositions.set(false))
      )
      .subscribe({
        next: res => {
          this.positions.set(res.data);
        },
      });
  }

  addContributor(user: Option, stepId: number) {
    if (stepId == null) {
      return;
    }
    this.isApplyingActionContributor.set(true);
    this.contributorsService
      .addContributor(this.flowId(), stepId, String(user.value ?? user.id))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isApplyingActionContributor.set(false))
      )
      .subscribe({
        next: () => {
          this.addContributorToList(user, stepId);
          this.userDropdownList.update(prev =>
            prev.filter(
              u =>
                this.contributorsService.getContributorKey(u) !==
                this.contributorsService.getContributorKey(user)
            )
          );
        },
      });
  }

  removeContributor(userId: number, stepId: number) {
    if (stepId == null) {
      return;
    }
    this.isApplyingActionContributor.set(true);
    this.contributorsService
      .removeContributor(this.flowId(), stepId, String(userId))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isApplyingActionContributor.set(false))
      )
      .subscribe({
        next: () => {
          this.removeContributorFromList(String(userId), stepId);
          this.contributorsDropdownList.update(prev =>
            prev.filter(
              u =>
                this.contributorsService.getContributorKey(u) !==
                this.contributorsService.getContributorKey({
                  value: `${userId}`,
                  label: '',
                })
            )
          );
        },
        complete: () => this.isApplyingActionContributor.set(false),
      });
  }

  addContributorToList(user: Option, stepId: number) {
    if (stepId == null) {
      return;
    }

    const userKey = this.contributorsService.getContributorKey(user);

    this.contributorsService.updateStepContributors(
      this.positions,
      stepId,
      contributors => {
        const alreadyExists = contributors.some(
          contributor =>
            this.contributorsService.getContributorKey(contributor) === userKey
        );

        if (alreadyExists) {
          return contributors;
        }

        this.isInAddMode.set(true);
        return [...contributors, user];
      }
    );
  }

  removeContributorFromList(
    userId: number | string,
    stepId: number | string | undefined
  ) {
    if (stepId == null) {
      return;
    }

    const userKey = String(userId);

    this.contributorsService.updateStepContributors(
      this.positions,
      stepId,
      contributors =>
        contributors.filter(
          contributor =>
            this.contributorsService.getContributorKey(contributor) !== userKey
        )
    );
  }

  getStepContributors(
    stepId: number | string | undefined,
    { currentPage, search }: { currentPage: number; search: string }
  ) {
    if (stepId == null) return;

    this.isLoadingDropdown.set(true);
    this.contributorsService
      .getStepContributors(this.flowId(), stepId, {
        name: search,
        page: currentPage,
      })
      .pipe(
        finalize(() => this.isLoadingDropdown.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        this.contributorsDropdownMeta.set(res.meta);
        this.contributorsDropdownList.update(prev => {
          const newOptions = res.data;
          if (currentPage === 1) {
            return newOptions;
          } else {
            return [...prev, ...newOptions];
          }
        });
      });
  }

  getUsers(
    { currentPage, search }: { currentPage: number; search: string },
    stepId: number
  ) {
    this.isLoadingDropdown.set(true);

    this.lookupService
      .userLookup({
        name: search,
        page: currentPage,
        filtersState: Status.ACTIVE,
        filtersNotContributedForStep: stepId,
      })
      .pipe(
        finalize(() => this.isLoadingDropdown.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        this.userDropdownMeta.set(res.meta);
        this.userDropdownList.update(prev => {
          const newOptions = res.data;
          if (currentPage === 1) {
            return newOptions;
          } else {
            return [...prev, ...newOptions];
          }
        });
      });
  }

  resetDropdown() {
    this.isInAddMode.set(false);
    this.userDropdownList.set([]);
    this.userDropdownMeta.set(null);
    this.contributorsDropdownList.set([]);
    this.contributorsDropdownMeta.set(null);
  }
}
